import { CalendarProvider } from "@/modules/calendar/context/calendarContext"
import { useCalendar } from "@/modules/calendar/hooks/useCalendar"
import { cn } from "@/modules/lib/utils"
import type { BlockSessionInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { TemplateWeekCalendarEvent } from "@/modules/template/components/calendar/templateWeekCalendarEvent"
import { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { useEditSessionInTemplate, useTemplateElement } from "@/modules/template/hooks/useTemplate"
import { useTemplateSessionEditFormSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import { TemplateType } from "@/modules/template/types/templateType"
import { Calendar } from "@modules/calendar/calendar"
import { type EventType, type ProgramCalendarEventType } from "@modules/calendar/calendarTypes"
import { useCallback, useMemo, useState } from "react"

type TemplateWeekCalendarProps = {
  mode: "preview" | "edit"
  templateId: string
  loading?: boolean
  className?: string
} & React.ComponentProps<"div">

export function TemplateWeekCalendar({ mode, templateId, loading, className, ...props }: TemplateWeekCalendarProps) {
  const { data: templateElement } = useTemplateElement(templateId)
  const [openedSessionId, setOpenedSessionId] = useState<string>()
  const { refreshCalendarEvents } = useCalendar()
  const { hideSidebar } = useSidebar()
  const { mutateAsync: editSessionInTemplate } = useEditSessionInTemplate()
  const { navigateToTemplateSessionEditForm } = useTemplateSessionEditFormSidebar()
  const { navigateToSingleBlockFormEdition, navigateToSingleBlockFormCreation } = useSessionSidebar()

  const sessions = useMemo<TemplateSessionEntity[]>(() => {
    if (templateElement.type !== TemplateType.WEEK) return []
    return templateElement.sessions
  }, [templateElement])

  const events = useMemo<ProgramCalendarEventType[]>(
    () => sessions.map((session) => TemplateSessionEntity.toCalendarEvent(session)),
    [sessions],
  )

  const onAddEventClick = (value?: Date | number) => {
    if (mode === "preview") return

    if (typeof value === "number") {
      navigateToTemplateSessionEditForm({ templateId, session: undefined, sessionIndex: value })
    } else {
      throw new Error(
        "Unexpected value type for onAddEventClick for template week session, expected number, got " + typeof value,
      )
    }
  }

  const onClickOnSlot = (session: TemplateSessionEntity) => {
    if (mode === "preview") return
    navigateToTemplateSessionEditForm({ templateId, session })
  }

  const onCreateBlockClick = (session: TemplateSessionEntity, newBlockType: SessionBlockType) => {
    if (mode === "preview") return
    setOpenedSessionId(session.id)
    navigateToSingleBlockFormCreation(session.blocks.length, newBlockType, async (newBlock: BlockSessionInputDto) => {
      const sessionInput = TemplateSessionEntity.fromEntityToInputDto(session)

      await editSessionInTemplate({
        templateId,
        sessionId: session.id,
        sessionInput: {
          ...sessionInput,
          blocks: [...sessionInput.blocks, newBlock],
        },
      })
      hideSidebar(false)
      refreshCalendarEvents()
    })
  }

  const onBlockEditClick = (session: TemplateSessionEntity, orderIndex: number) => {
    if (mode === "preview") return
    const block = session.blocks[orderIndex]
    if (!block) return
    setOpenedSessionId(session.id)
    navigateToSingleBlockFormEdition(block, orderIndex, async (updatedBlock: BlockSessionInputDto) => {
      const sessionInput = TemplateSessionEntity.fromEntityToInputDto(session)

      await editSessionInTemplate({
        templateId,
        sessionId: session.id,
        sessionInput: {
          ...sessionInput,
          blocks: sessionInput.blocks.map((block) => {
            if (
              updatedBlock.type === SessionBlockType.SESSION_EXERCISE &&
              block.type === SessionBlockType.SESSION_EXERCISE &&
              block.content.orderIndex === updatedBlock.content.orderIndex
            ) {
              return updatedBlock
            }
            return block
          }),
        },
      })
      hideSidebar(false)
      refreshCalendarEvents()
    })
  }

  const renderEvent = useCallback(
    (index: number, event: EventType) => {
      const session = sessions.find((s) => s.id === event.id)
      const templateEvent = events.find((e) => e.id === event.id)
      if (!session || !templateEvent) return null

      return (
        <TemplateWeekCalendarEvent
          key={session.id}
          templateId={templateId}
          index={index}
          event={templateEvent}
          session={session}
          onCreateBlockClick={onCreateBlockClick}
          onBlockEditClick={onBlockEditClick}
          onEventClick={onClickOnSlot}
          isDetailsOpen={openedSessionId === event.id}
          mode={mode}
          displayDetailedEvents={true}
        />
      )
    },
    [sessions, events, onCreateBlockClick, onBlockEditClick, onClickOnSlot, openedSessionId, mode, templateId],
  )

  return (
    <CalendarProvider
      isWeekTemplate={false}
      isUndated={true}
      initialDate={undefined}
      defaultDisplayDetailedEvents={true}
      defaultView="dayGridWeek"
    >
      <div className={cn("mb-8 flex grow flex-col content-center", className)} {...props}>
        <Calendar
          calendarEvents={events}
          loading={loading}
          canAddEvents={true}
          onAddEventClick={onAddEventClick}
          eventLimitPerDay={1}
          isDragAndDropActive={false}
          customRenderEvent={renderEvent}
        />
      </div>
    </CalendarProvider>
  )
}
